import { Injectable } from '@angular/core';
import { Track, TrackCues } from '@app/core/models/Track';
import { AuthState } from '@app/core/states/auth.state';
import { RadioState } from '@app/core/states/radio.state';
import { TrackCuesService } from '@app/library/services/track-cues.service';
import { TracksStateModel } from '@app/library/states/tracks.state';
import { Action, Selector, State, StateContext, StateOperator, Store } from '@ngxs/store';
import { compose, iif, patch } from '@ngxs/store/operators';
import { Observable } from 'rxjs';
import { catchError, filter, mergeMap, take } from 'rxjs/operators';

import {
  CalculateTrackCuesFailure,
  CalculateTrackCuesRequest,
  CalculateTrackCuesSuccess,
  DeleteNextTrackCues,
  DeletePreviousTrackCues,
  NextTrackCuesFailure,
  NextTrackCuesRequest,
  NextTrackCuesSuccess,
  PreviousTrackCuesFailure,
  PreviousTrackCuesRequest,
  PreviousTrackCuesSuccess,
  SaveNextTrack,
  SavePreviousTrack,
  TrackCuesFailure,
  TrackCuesRequest,
  TrackCuesSuccess,
  UpdateTrackCuesFailure,
  UpdateTrackCuesRequest,
  UpdateTrackCuesSuccess,
} from './track-mix-points.actions';

enum TrackMixPointType {
  PREVIOUS = 'previousTrack',
  NEXT = 'nextTrack',
}

export class TrackMixPointsStateModel {
  mixTracks: {
    [radioId: string]: {
      previousTrack?: Track;
      nextTrack?: Track;
    };
  };
  previousCues: TrackCues;
  nextCues: TrackCues;
}

@State<TrackMixPointsStateModel>({
  name: 'trackMixPoints',
  defaults: {
    mixTracks: {},
    previousCues: null,
    nextCues: null,
  },
})
@Injectable()
export class TrackMixPointsState {
  constructor(
    private readonly store: Store,
    private readonly trackCuesService: TrackCuesService,
  ) {}
  @Selector()
  static previousTrack(state: TrackMixPointsStateModel): (radioId: number) => Track {
    return radioId => {
      const radioSelector = `radio${radioId}`;

      return state.mixTracks[radioSelector]
        ? state.mixTracks[radioSelector].previousTrack
        : null;
    };
  }

  @Selector()
  static nextTrack(state: TrackMixPointsStateModel): (radioId: number) => Track {
    return radioId => {
      const radioSelector = `radio${radioId}`;

      return state.mixTracks[radioSelector]
        ? state.mixTracks[radioSelector].nextTrack
        : null;
    };
  }

  @Selector()
  static previousCues(state: TrackMixPointsStateModel) {
    return state.previousCues;
  }

  @Selector()
  static nextCues(state: TrackMixPointsStateModel) {
    return state.nextCues;
  }

  @Action(TrackCuesRequest)
  getTrackCues(ctx: StateContext<TracksStateModel>, { track }: TrackCuesRequest) {
    return this.getCurrentRadioId().pipe(
      mergeMap(id => this.trackCuesService.getTrackCues(id, track.id)),
      mergeMap(data => ctx.dispatch(new TrackCuesSuccess(track, data))),
      catchError(err => ctx.dispatch(new TrackCuesFailure(err))),
    );
  }

  @Action(PreviousTrackCuesRequest)
  getPreviousTrackCues(
    ctx: StateContext<TracksStateModel>,
    { track }: PreviousTrackCuesRequest,
  ) {
    return this.getCurrentRadioId().pipe(
      mergeMap(id => this.trackCuesService.getTrackCues(id, track.id)),
      mergeMap(data => ctx.dispatch(new PreviousTrackCuesSuccess(track, data))),
      catchError(err => ctx.dispatch(new PreviousTrackCuesFailure(err))),
    );
  }

  @Action(PreviousTrackCuesSuccess)
  prevSuccess(
    ctx: StateContext<TrackMixPointsStateModel>,
    { track, cues }: PreviousTrackCuesSuccess,
  ) {
    const authToken = this.store.selectSnapshot(AuthState.getAccessToken);
    const previousTrack: TrackCues = {
      ...cues,
      audioUrl: `${track.file}?token=${authToken}`,
    };
    ctx.setState(
      patch({
        previousCues: previousTrack,
      }),
    );
  }

  @Action(DeletePreviousTrackCues)
  deletePrevCues(ctx: StateContext<TrackMixPointsStateModel>) {
    ctx.setState(
      patch({
        previousCues: null,
      }),
    );
  }

  @Action(NextTrackCuesRequest)
  getNextTrackCues(ctx: StateContext<TracksStateModel>, { track }: NextTrackCuesRequest) {
    return this.getCurrentRadioId().pipe(
      mergeMap(id => this.trackCuesService.getTrackCues(id, track.id)),
      mergeMap(data => ctx.dispatch(new NextTrackCuesSuccess(track, data))),
      catchError(err => ctx.dispatch(new NextTrackCuesFailure(err))),
    );
  }

  @Action(NextTrackCuesSuccess)
  nextSuccess(
    ctx: StateContext<TrackMixPointsStateModel>,
    { track, cues }: NextTrackCuesSuccess,
  ) {
    const authToken = this.store.selectSnapshot(AuthState.getAccessToken);
    const nextTrack: TrackCues = {
      ...cues,
      audioUrl: `${track.file}?token=${authToken}`,
    };
    ctx.setState(
      patch({
        nextCues: nextTrack,
      }),
    );
  }

  @Action(DeleteNextTrackCues)
  deleteNextCues(ctx: StateContext<TrackMixPointsStateModel>) {
    ctx.setState(
      patch({
        nextCues: null,
      }),
    );
  }

  @Action(UpdateTrackCuesRequest)
  updateTrackCues(
    ctx: StateContext<TracksStateModel>,
    { track, cues }: UpdateTrackCuesRequest,
  ) {
    return this.getCurrentRadioId().pipe(
      mergeMap(id => this.trackCuesService.updateTrackCues(id, track.id, cues)),
      mergeMap(data => ctx.dispatch(new UpdateTrackCuesSuccess(track, data))),
      catchError(err => ctx.dispatch(new UpdateTrackCuesFailure(err))),
    );
  }

  @Action(CalculateTrackCuesRequest)
  calculateTrackCues(
    ctx: StateContext<TrackMixPointsStateModel>,
    { track }: CalculateTrackCuesRequest,
  ) {
    return this.getCurrentRadioId().pipe(
      mergeMap(id => this.trackCuesService.calculateTrackCues(id, track.id)),
      mergeMap(data => ctx.dispatch(new CalculateTrackCuesSuccess(data))),
      catchError(err => ctx.dispatch(new CalculateTrackCuesFailure(err))),
    );
  }

  @Action(SavePreviousTrack)
  savePreviousTrack(
    ctx: StateContext<TrackMixPointsStateModel>,
    { track }: SavePreviousTrack,
  ) {
    ctx.setState(this.setTrack(track, TrackMixPointType.PREVIOUS));
  }

  @Action(SaveNextTrack)
  saveNextTrack(
    ctx: StateContext<TrackMixPointsStateModel>,
    { track }: SavePreviousTrack,
  ) {
    ctx.setState(this.setTrack(track, TrackMixPointType.NEXT));
  }

  setTrack(
    track: Track,
    trackType: TrackMixPointType,
  ): StateOperator<TrackMixPointsStateModel> {
    const radioId = this.store.selectSnapshot(RadioState.currentRadioId);
    const radioSelector = `radio${radioId}`;
    const createRadioIfNone = patch<TrackMixPointsStateModel>({
      // @ts-ignore
      mixTracks: iif(val => !val[radioSelector], patch({ [radioSelector]: {} })),
    });
    const updateRules = patch<TrackMixPointsStateModel>({
      mixTracks: patch({
        [radioSelector]: patch({
          [trackType]: track,
        }),
      }),
    });

    return compose(createRadioIfNone, updateRules);
  }

  getCurrentRadioId(): Observable<number> {
    return this.store.select(RadioState.currentRadioId).pipe(
      filter(data => !!data),
      take(1),
    );
  }
}
